import { rutinInfos } from '../Features/Delsystem/_Common/Const/RutinInfo';
import { DelsystemIdInterval } from '../Features/Delsystem/_Common/Const/DelsystemIdInterval';
import { GraphQLError } from 'graphql';
import Rollbar, { LogArgument } from 'rollbar';
import { diffMin } from './dateUtils';
import { setGlobalRollbarId } from '../Components/InternalError/RollbarIdContext';

let w: any = undefined;
if (typeof window !== 'undefined') {
  w = window;
}

export enum Environment {
  featureBranch = 'feature branch', //testbyggen från brancher
  test = 'test', //test-installationen hos Eniac
  releaseCandidate = 'release candidate', //nästa uppdatering hos Eniac
  distr = 'distr', //distr hos Eniac
  production = 'production', //kund installationer
  ovrigtEniac = 'övrigt eniac', //programmerarnas utvecklingsmiljö och ev. övriga installationer hos Eniac
  rollbarTest = 'rollbarTest', //kan sättas när man testar rollbar. Kan rapportera även från dev-byggen och vi håller fel som är fejkade eller ev. rapporteras på sitt eget sätt vid sidan av andra rapporteringar.
}
interface ILogConfig {
  /** custom fingerprint, felrapporteringar med samma fingerprint kommer alltid grupperas i rollbar (annars sker en gruppering enligt rollbars algoritmer och inställningar inne i projektet) */
  fingerprint?: string;
  /** sträng för hjälp i felsökning som beskriver var felrapporteringen sker till exempel en ErrorBoundary med visst namn */
  reporter?: string;
}

const isProduction = (): boolean => {
  let result = false;
  try {
    result = import.meta.env.MODE === 'production';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('isProduction', error);
  }
  return result;
};

export const getRollbarEnvironment = () => {
  if (!isProduction()) {
    if (window.env?.environment === Environment.rollbarTest) {
      return Environment.rollbarTest;
    }
    return Environment.ovrigtEniac;
  }
  if (window.env == null || !window.env.environment) {
    return Environment.production;
  } else {
    return window.env.environment;
  }
};

export const reportToRollbar = () =>
  (isProduction() || w?.env?.environment === Environment.rollbarTest) &&
  w?.Rollbar != null;

type LogLevel = 'critical' | 'error' | 'warning' | 'info' | 'debug';
const defaultLogLevel: LogLevel = 'error';

interface IErrorEnvironment {
  rutin: string;
  delsystem: string;
}

const getDelsystem = (rutin: string) => {
  if (!rutin) {
    return '';
  }
  return Object.keys(rutinInfos).reduce((a, b) => {
    const info = rutinInfos[b];
    if (info.rutinURL === rutin) {
      return DelsystemIdInterval[Math.floor((info.rutinID - 1) / 1000)];
    }
    return a;
  }, '');
};

export const getErrorEnvironment = (): IErrorEnvironment => {
  const urlWithQueryString = window.location.href;
  const nonRoutine = urlWithQueryString[urlWithQueryString.length - 2] === '#';
  const url = urlWithQueryString.split('?')?.[0];
  const rutinMatch = url.match('/Rutin/([^/]*)');
  const rutin = nonRoutine
    ? 'Startsidan'
    : rutinMatch
    ? rutinMatch[1]
    : url.slice(url.lastIndexOf('/') + 1);
  return {
    rutin: rutin,
    delsystem: rutinMatch ? getDelsystem(rutin) : '',
  };
};

/** Skapar ett Error med name och message
 * @param name Namn för felet (Används för att gruppera felet. Fel med olika namn grupperas normalt inte ihop. Använd en sträng som hade kunnat vara en identifier då t.ex. mellanslag inte hanteras bra av rollbar som kanske utgår från att det är ett klassnamn)
 * @param message Felmeddelande (Orsaken till felet i klartext. Används inte för att gruppera felet)
 */
export const namedError = (
  name: string,
  message: string,
  cause?: unknown
): Error => {
  const error = Error(message, { cause });
  error.name = name;
  (error as unknown as Record<string, unknown>)['class'] = name;
  return error;
};

/** Skapar ett fel utifrån en lista av GraphQL-fel. Samma som första felet om listan är en post lång */
export const graphQLError = (errors: readonly GraphQLError[]): Error => {
  if (errors.length === 0) {
    return namedError(
      'EmptyGraphQLErrorList',
      'Felrapportering av tom lista med GraphQL-fel'
    );
  }
  if (errors.length === 1) {
    return errors[0];
  }
  const error = namedError(
    'MultipleGraphQlErrors',
    errors.map(error => error.message).join(' | ')
  );
  (error as unknown as Record<string, string | undefined>)['trace'] = (errors[0] as unknown as Record<string, string | undefined>)?.['trace'];
  return error;
};

/** Fel som upprepas många gånger utan att vi behöver veta varje occurence, skickas bara max en gång per 5 minuter */
const limitedReportingErrors = ['NetworkError', 'NetworkError503'];

const lastReportTimes = new Map<string, Date>();
export default class Logger {
  static log = (
    /** error, warning, info eller debug */
    logLevel: LogLevel,
    /** Namn på felet, används för att gruppera fel. Om undefined så används error.name. Använd inte mellanslag*/
    name: string | undefined,
    /** Meddelande */
    message: string,
    /** Fel som ska loggas */
    error?: Error,
    /** Extra poster som skickas med till rollbar */
    extraArgs?: object,
    /** Config */
    config?: ILogConfig
  ): Promise<string | null> => {
    return new Promise(resolve => {
      let alreadyReported = false;
      if (name) {
        const lastReportTime = lastReportTimes.get(name);
        alreadyReported =
          !!lastReportTime && diffMin(lastReportTime, new Date()) < 5;

        lastReportTimes.set(name, new Date());
      }
      if (reportToRollbar()) {
        if (alreadyReported && name && limitedReportingErrors.includes(name)) {
          // eslint-disable-next-line no-console
          console.log(name, 'already reported to rollbar', lastReportTimes);
          resolve(null);
        } else {
          // eslint-disable-next-line no-console
          console.log(
            'reportToRollbar',
            logLevel,
            'name:',
            name,
            'message:',
            message,
            'error:',
            error,
            'extraArgs:',
            extraArgs
          );
          const errorToLog =
            error ?? namedError(name ?? 'ErrorMessage', message);
          if (name) {
            errorToLog.name = name;
            (errorToLog as unknown as Record<string, unknown>)['class'] = name;
          }
          const rollbar: Rollbar = w.Rollbar;
          //spara temporära inställningar
          rollbar.configure({
            logLevel: logLevel,
            payload: {
              fingerprint: config?.fingerprint ?? name,
            },
          });
          rollbar.log(
            message,
            errorToLog,
            { reporter: config?.reporter, ...extraArgs },
            (err, resp) => {
              const status = err ? 'error' : resp ? 'success' : 'no response';
              // eslint-disable-next-line no-console
              console.log(
                'reporting error to rollbar result',
                'status:',
                status,
                'response:',
                resp,
                'error:',
                err
              );

              const uuid = resp?.result?.uuid || null;
              setGlobalRollbarId(uuid);
              resolve(uuid);
            }
          );
          //återställ temporära inställningar
          rollbar.configure({
            logLevel: defaultLogLevel,
            payload: {
              fingerprint: '',
            },
          });
        }
      } else {
        switch (logLevel) {
          case 'error':
            // eslint-disable-next-line no-console
            console.error(name, message);
            break;
          case 'warning':
            // eslint-disable-next-line no-console
            console.warn(name, message);
            break;
          case 'info':
            // eslint-disable-next-line no-console
            console.info(name, message);
            break;
          case 'debug':
            // eslint-disable-next-line no-console
            console.debug(name, message);
            break;
          default:
            // eslint-disable-next-line no-console
            console.log(name, message);
        }
        resolve(null);
      }
    });
  };

  static critical = (error: Error | string, arg?: object) => {
    if (typeof error === 'string') {
      this.log('critical', 'UntypedError', error, undefined, arg);
    } else {
      this.log('critical', undefined, error.message, error, arg);
    }
  };

  static error = (error: Error | string, arg?: object) => {
    if (typeof error === 'string') {
      this.log('error', 'UntypedError', error, undefined, arg);
    } else {
      this.log('error', undefined, error.message, error, arg);
    }
  };

  static warn = (error: Error, arg?: object) => {
    if (typeof error === 'string') {
      this.log('warning', 'UntypedError', error, undefined, arg);
    } else {
      this.log('warning', error.name, error.message, error, arg);
    }
  };

  static info = (error: Error | string, arg?: object) => {
    if (typeof error === 'string') {
      this.log('info', 'UntypedError', error, undefined, arg);
    } else {
      this.log('info', undefined, error.message, error, arg);
    }
  };

  static debug = (error: Error | string, arg?: LogArgument[]) => {
    if (typeof error === 'string') {
      this.log('debug', 'UntypedError', error, undefined, arg);
    } else {
      this.log('debug', undefined, error.message, error, arg);
    }
  };
}
